import type { LucideIcon } from 'lucide-react';
import {
  BarChart3,
  Radio,
  Crosshair,
  FileText,
  Settings,
  Radar,
  Bot,
  ShieldCheck,
  Building2,
} from 'lucide-react';

/**
 * ──────────────────────────────────────────────────────────────────────────
 * Navigation registry — the SINGLE source of truth for the group → child-page
 * system. Both the sidebar (AuthShell: icon + hover flyout) and the in-page tab
 * strip (TabShell) are derived from this one list, and App.tsx nests each
 * multi-page group's routes under a shared <TabShell/>. Change a label/path/order
 * here and every surface follows — no per-view hardcoded tab bars.
 *
 * A "group" is one sidebar icon:
 *   - 1 visible page  → plain icon (tooltip), no tab strip.
 *   - >1 visible page → icon + hover flyout, and a tab strip inside TabShell.
 *
 * Role visibility is declarative (group- and page-level). The role-specific Org
 * Settings route (admin vs manager) is just two role-gated pages with the same
 * label, so the registry stays one list rather than three.
 * ──────────────────────────────────────────────────────────────────────────
 */

export type Role = 'admin' | 'manager' | 'trader';

// 'trade' = always-available core trader nav; 'alpha' = the password-gated
// dev/alpha zone (analytics + experimental, tested live on prod); 'manage' =
// role-gated org/admin. Section changes drive the sidebar dividers.
export type NavSection = 'manage' | 'trade' | 'alpha';

/** A child page within a group. Groups with >1 visible page show a tab strip. */
export interface NavPage {
  id: string;
  label: string;
  /** Absolute route path. Also how TabShell + the sidebar derive active state. */
  path: string;
  /** Small corner marker (e.g. 'test'). */
  badge?: string;
  /** Roles that can see this page. Omit ⇒ inherit the group's roles. */
  roles?: Role[];
}

/** A nav group = one sidebar icon + its child page(s). */
export interface NavGroup {
  id: string;
  label: string;
  icon: LucideIcon;
  section: NavSection;
  /** Small corner marker on the icon (e.g. 'g10'). */
  badge?: string;
  /** Roles that can see this group. Omit ⇒ all roles. */
  roles?: Role[];
  /** Optional group-level toolbar rendered on the right of the TabShell header. */
  toolbar?: 'org';
  /** Render this group in the sidebar footer region instead of the main column. */
  footer?: boolean;
  pages: NavPage[];
}

export const NAV_GROUPS: NavGroup[] = [
  // ── manage (role-gated) ──────────────────────────────────────────────
  {
    id: 'administration',
    label: 'Administration',
    icon: ShieldCheck,
    section: 'manage',
    roles: ['admin'],
    pages: [
      { id: 'management', label: 'Management', path: '/administration/management' },
      { id: 'g1-health', label: 'G1 Health', path: '/administration/g1-health' },
    ],
  },
  {
    id: 'organization',
    label: 'Organization',
    icon: Building2,
    section: 'manage',
    roles: ['admin', 'manager'],
    toolbar: 'org',
    // One canonical Org Settings route for both roles — the page itself switches
    // to admin mode (org switcher) by role, so there's no role-specific path.
    pages: [
      { id: 'org-settings', label: 'Org Settings', path: '/organization/org-settings' },
      { id: 'org-account', label: 'Org Account', path: '/organization/org-account' },
      { id: 'exports', label: 'Exports', path: '/organization/exports' },
    ],
  },

  // ── trade (everyone, single-page icons) ──────────────────────────────
  { id: 'dashboard', label: 'Value Dashboard', icon: BarChart3, section: 'trade', pages: [{ id: 'dashboard', label: 'Value Dashboard', path: '/dashboard' }] },
  { id: 'positions', label: 'Position Tracker', icon: Crosshair, section: 'trade', pages: [{ id: 'positions', label: 'Position Tracker', path: '/positions' }] },
  { id: 'live', label: 'Live Discovery', icon: Radio, section: 'trade', pages: [{ id: 'live', label: 'Live Discovery', path: '/live' }] },
  { id: 'trades', label: 'My Trades', icon: FileText, section: 'trade', pages: [{ id: 'trades', label: 'My Trades', path: '/trades' }] },

  // ── alpha (research = everyone; bots = role-gated) ───────────────────
  {
    id: 'research',
    label: 'Research',
    icon: Radar,
    section: 'alpha',
    pages: [
      { id: 'g3', label: 'G3 — Analytics', path: '/research/g3' },
      { id: 'g4', label: 'G4 — Lab', path: '/research/g4' },
      { id: 'streamrift', label: 'StreamRift', path: '/research/streamrift' },
    ],
  },
  {
    id: 'bots',
    label: 'Bots',
    icon: Bot,
    section: 'alpha',
    badge: 'g10',
    roles: ['admin', 'manager'],
    pages: [
      { id: 'vanilla', label: 'Vanilla MM', path: '/bots/vanilla' },
      { id: 'recycler', label: 'Recycler', path: '/bots/recycler' },
      { id: 'g9', label: 'G9 — Auto Assist', path: '/bots/g9', badge: 'test' },
    ],
  },

  // ── footer ───────────────────────────────────────────────────────────
  { id: 'settings', label: 'Settings', icon: Settings, section: 'trade', footer: true, pages: [{ id: 'settings', label: 'Settings', path: '/settings' }] },
];

/** Does `pathname` fall within `path` (exact or a sub-route)? */
export function pathMatches(pathname: string, path: string): boolean {
  return pathname === path || pathname.startsWith(path + '/');
}

/** Role-filter the registry: drop groups/pages the role can't see, then drop
 *  any group left with no visible pages. Returns groups in registry order. */
export function navForRole(role: Role): NavGroup[] {
  return NAV_GROUPS.filter((g) => !g.roles || g.roles.includes(role))
    .map((g) => ({ ...g, pages: g.pages.filter((p) => !p.roles || p.roles.includes(role)) }))
    .filter((g) => g.pages.length > 0);
}

/** The group that owns the current path — for TabShell to render the right
 *  tab strip/toolbar. Pass the already role-filtered groups. */
export function groupForPath(groups: NavGroup[], pathname: string): NavGroup | undefined {
  return groups.find((g) => g.pages.some((p) => pathMatches(pathname, p.path)));
}

// Back-compat alias for the layout barrel (no live importers).
export type AppView = string;
